// File: src/components/auth/AdminRoute.tsx
// FINAL CLEAN VERSION. Rebuilt with correct syntax and integrated with the theme.

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material';

interface AdminRouteProps {
    children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
    const { user, isLoading, error } = useUser();
    const { t } = useTranslation();

    // While loading user data, show a themed loading indicator.
    if (isLoading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    {t('auth.loading')}
                </Typography>
            </Container>
        );
    }

    // If an error occurred fetching the user, show a themed error message.
    if (error) {
        return (
            <Container sx={{ mt: 4 }}>
                <Alert severity="error">
                    <Typography variant="h6" component="div">
                        {t('auth.authentication_error')}
                    </Typography>
                    <Typography variant="body2">
                        {error} — {t('auth.please_login_again')}
                    </Typography>
                </Alert>
            </Container>
        );
    }

    // If the user exists and is an admin, show the protected content.
    if (user && user.is_admin) {
        return <>{children}</>;
    }

    // If the user is not an admin or doesn't exist, redirect to the dashboard.
    return <Navigate to="/dashboard" replace />;
};

export default AdminRoute;
